import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { SettingsSection } from '@/features/settings/settings-section';
import { userStore } from '@/stores/user.store';
import { formatDateLocale } from '@/utils/date';

import { teamApis } from '../team.query';
import { teamStore } from '../team.store';
import {
  TeamRoleEnum,
  type MyTeam,
  type TeamBillingStatus,
} from '../team.types';

import { TeamUsageCreditsCard } from './TeamUsageCreditsCard';
import { TransferOwnershipModal } from './TransferOwnershipModal';

interface TeamBillingCardProps {
  team: MyTeam;
  billing: TeamBillingStatus;
  isAdmin: boolean;
}

function formatPeriodEnd(unixSeconds: number): string {
  return formatDateLocale(new Date(unixSeconds * 1000), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export const TeamBillingCard = observer(function TeamBillingCard({
  team,
  billing,
  isAdmin,
}: TeamBillingCardProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const isOwner = team.role === TeamRoleEnum.enum.owner;

  const hasSubscription =
    billing.stripe_subscription_status &&
    ['active', 'trialing', 'past_due'].includes(
      billing.stripe_subscription_status
    );

  return (
    <YStack className="gap-6">
      <YStack className="bg-surface border border-border dark:border-border-strong rounded-xl px-6 py-5 gap-5">
        {hasSubscription ? (
          <YStack className="gap-4">
            <XStack className="items-start justify-between gap-4">
              <YStack className="gap-1 min-w-0">
                <span className="text-xl font-semibold tracking-tight text-foreground">
                  {billing.seat_count}{' '}
                  {billing.seat_count === 1 ? 'seat' : 'seats'}
                </span>
                {(billing.plus_seats > 0 ||
                  billing.power_seats > 0 ||
                  billing.pro_seats > 0 ||
                  billing.pro2_seats > 0) && (
                  <span className="text-sm text-foreground-muted">
                    {[
                      billing.plus_seats > 0 && `${billing.plus_seats} Plus`,
                      billing.power_seats > 0 && `${billing.power_seats} Power`,
                      billing.pro_seats > 0 && `${billing.pro_seats} Pro`,
                      billing.pro2_seats > 0 && `${billing.pro2_seats} Pro2`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                )}
              </YStack>
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => teamApis.createPortal(team.id)}
                  isLoading={teamStore.portalLoading}
                  className="shrink-0"
                >
                  <Icons.externalLink className="w-3.5 h-3.5" />
                  Manage Billing
                </Button>
              )}
            </XStack>

            {(billing.current_period_end ||
              billing.billing_interval ||
              billing.credit_balance_cents > 0) && (
              <YStack className="gap-1.5 text-sm">
                {billing.current_period_end && (
                  <XStack className="items-center gap-4">
                    <span className="text-foreground-muted w-28 shrink-0">
                      {billing.cancel_at_period_end ? 'Cancels' : 'Renews'}
                    </span>
                    <span className="text-foreground">
                      {formatPeriodEnd(billing.current_period_end)}
                    </span>
                  </XStack>
                )}
                {billing.billing_interval && (
                  <XStack className="items-center gap-4">
                    <span className="text-foreground-muted w-28 shrink-0">
                      Billing
                    </span>
                    <span className="text-foreground capitalize">
                      {billing.billing_interval}
                    </span>
                  </XStack>
                )}
                {billing.credit_balance_cents > 0 && (
                  <XStack className="items-center gap-4">
                    <span className="text-foreground-muted w-28 shrink-0">
                      Credit balance
                    </span>
                    <span className="text-foreground">
                      ${(billing.credit_balance_cents / 100).toFixed(2)}
                    </span>
                  </XStack>
                )}
              </YStack>
            )}

            {billing.cancel_at_period_end && (
              <XStack className="items-center gap-2 rounded-lg border border-warning-5 dark:border-warning-3 bg-warning-2 px-3 py-2">
                <Icons.warnTriangle className="w-3.5 h-3.5 text-warning-11 shrink-0" />
                <span className="text-sm text-warning-11">
                  Subscription will cancel at end of billing period
                </span>
              </XStack>
            )}
          </YStack>
        ) : (
          <XStack className="items-center justify-between gap-4">
            <YStack className="gap-1 min-w-0">
              <span className="text-xl font-semibold tracking-tight text-foreground">
                No active subscription
              </span>
              <span className="text-sm text-foreground-muted">
                {isAdmin
                  ? 'Resubscribe to restore access for all team members.'
                  : 'Ask an admin to resubscribe to restore access for all team members.'}
              </span>
            </YStack>
            {isAdmin && (
              <Button
                variant="default"
                size="sm"
                onClick={() =>
                  teamApis.createCheckout({
                    name: team.name,
                    billing_interval: billing.billing_interval ?? 'monthly',
                    owner_tier: team.tier,
                  })
                }
                isLoading={teamStore.checkoutLoading}
                loadingText="Redirecting..."
                className="shrink-0"
              >
                Resubscribe
              </Button>
            )}
          </XStack>
        )}
      </YStack>

      <TeamUsageCreditsCard teamId={team.id} />

      {isOwner && (
        <SettingsSection.Group title="Danger Zone">
          <SettingsSection.Item direction="horizontal">
            <SettingsSection.ItemHeader>
              <SettingsSection.ItemLabel>
                Transfer ownership
              </SettingsSection.ItemLabel>
              <SettingsSection.ItemDescription>
                Make another member the owner. You will become an admin.
              </SettingsSection.ItemDescription>
            </SettingsSection.ItemHeader>
            <Button
              variant="secondary"
              className="w-24"
              onClick={() => setShowTransfer(true)}
            >
              Transfer
            </Button>
          </SettingsSection.Item>
          <SettingsSection.Item direction="horizontal">
            <SettingsSection.ItemHeader>
              <SettingsSection.ItemLabel>
                Delete this team
              </SettingsSection.ItemLabel>
              <SettingsSection.ItemDescription>
                This will cancel the subscription and remove all members.
              </SettingsSection.ItemDescription>
            </SettingsSection.ItemHeader>
            <Button
              variant="secondary"
              className="w-24"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete
            </Button>
          </SettingsSection.Item>
        </SettingsSection.Group>
      )}

      <TransferOwnershipModal
        open={showTransfer}
        onOpenChange={setShowTransfer}
        teamId={team.id}
        members={teamStore.dashboard?.members ?? []}
        currentUserEmail={userStore.user?.email}
      />

      <Dialog.Root open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <Dialog.Content className="max-w-sm">
          <Dialog.Header>
            <Dialog.Title>Delete {team.name}?</Dialog.Title>
            <Dialog.Description>
              This action is irreversible. The team subscription will be
              canceled, all members will be downgraded to the free plan, and the
              team will be permanently deleted.
            </Dialog.Description>
          </Dialog.Header>
          <XStack className="items-start gap-2 rounded-lg border border-danger-5 dark:border-danger-3 bg-danger-2 px-3 py-2.5">
            <Icons.warnTriangle className="w-3.5 h-3.5 text-danger-11 shrink-0 mt-0.5" />
            <span className="text-sm text-danger-11">
              This cannot be undone. All team data will be permanently removed.
            </span>
          </XStack>
          <Dialog.Footer>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              isLoading={deleting}
              onClick={async () => {
                setDeleting(true);
                try {
                  await teamApis.deleteTeam(team.id);
                  setShowDeleteConfirm(false);
                } finally {
                  setDeleting(false);
                }
              }}
            >
              Delete Team
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>
    </YStack>
  );
});
